import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Sparkles, Send, FileText, AlertTriangle, Target, Users,
  Calendar, MessageSquare, Clock, Zap, Brain, ArrowLeft,
  ChevronDown, RefreshCw, Copy, TrendingUp, ListTodo
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { aiAPI, projectAPI } from '../api';
import { Loader } from '../App.core';

// Simple markdown-to-html renderer
const renderMarkdown = (text) => {
  if (!text) return '';
  return text
    .replace(/^### (.*$)/gim, '<h4 style="font-weight:600;margin:16px 0 8px;font-size:0.95rem;">$1</h4>')
    .replace(/^## (.*$)/gim, '<h3 style="font-weight:650;margin:20px 0 10px;font-size:1.05rem;color:var(--px-text);">$1</h3>')
    .replace(/^# (.*$)/gim, '<h2 style="font-weight:700;margin:24px 0 12px;font-size:1.15rem;">$1</h2>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/^- (.*$)/gim, '<div style="padding-left:16px;margin:3px 0;"><span style="color:var(--px-primary);margin-right:6px;">•</span>$1</div>')
    .replace(/^\d+\. (.*$)/gim, '<div style="padding-left:16px;margin:3px 0;">$1</div>')
    .replace(/\|(.+)\|/g, (match) => {
      const cells = match.split('|').filter(c => c.trim());
      if (cells.every(c => c.trim().match(/^[-:]+$/))) return '';
      return `<div style="display:flex;gap:12px;padding:4px 0;border-bottom:1px solid var(--px-border);font-size:0.84rem;">${cells.map(c => `<span style="flex:1;">${c.trim()}</span>`).join('')}</div>`;
    })
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
};

// AI Feature Cards
const AI_FEATURES = [
  { key: 'summary', label: 'Project Summary', icon: FileText, desc: 'Executive overview with health status', color: '#3b82f6', requiresProject: true },
  { key: 'risks', label: 'Risk Analysis', icon: AlertTriangle, desc: 'Identify risks, blockers & mitigation', color: '#ef4444', requiresProject: true },
  { key: 'status', label: 'Status Report', icon: TrendingUp, desc: 'Weekly status for standup/email', color: '#059669', requiresProject: true },
  { key: 'tasks', label: 'Suggest Tasks', icon: ListTodo, desc: 'AI-recommended tasks to create', color: '#8b5cf6', requiresProject: true },
  { key: 'workload', label: 'Optimize Workload', icon: Users, desc: 'Rebalance team assignments', color: '#f59e0b', requiresProject: true },
  { key: 'meeting', label: 'Meeting Notes', icon: MessageSquare, desc: 'Generate standup/review notes', color: '#06b6d4', requiresProject: true },
  { key: 'timeline', label: 'Timeline Forecast', icon: Clock, desc: 'Predict completion dates', color: '#ec4899', requiresProject: true },
  { key: 'digest', label: 'Daily Digest', icon: Zap, desc: 'Your personalized daily brief', color: '#10b981', requiresProject: false },
];

export const AIAssistantPage = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [activeFeature, setActiveFeature] = useState(null);
  const [aiAvailable, setAiAvailable] = useState(true);
  const [history, setHistory] = useState([]);
  const resultRef = useRef(null);

  useEffect(() => {
    projectAPI.list({ limit: 100, status: 'active' })
      .then(({ data }) => setProjects(data.projects))
      .catch(() => { });
    aiAPI.status()
      .then(({ data }) => setAiAvailable(data.available))
      .catch(() => setAiAvailable(false));
  }, []);

  useEffect(() => {
    if (resultRef.current) resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [result]);

  const handleFeature = async (featureKey) => {
    if (featureKey !== 'digest' && !selectedProject) {
      toast.error('Please select a project first.');
      return;
    }
    setLoading(true);
    setActiveFeature(featureKey);
    setResult(null);
    try {
      let res;
      switch (featureKey) {
        case 'summary': res = await aiAPI.projectSummary(selectedProject); break;
        case 'risks': res = await aiAPI.riskAnalysis(selectedProject); break;
        case 'status': res = await aiAPI.statusReport(selectedProject); break;
        case 'tasks': res = await aiAPI.suggestTasks(selectedProject); break;
        case 'workload': res = await aiAPI.optimizeWorkload(selectedProject); break;
        case 'meeting': res = await aiAPI.meetingNotes(selectedProject, { meeting_type: 'Daily Standup' }); break;
        case 'timeline': res = await aiAPI.timelinePrediction(selectedProject); break;
        case 'digest': res = await aiAPI.dailyDigest(); break;
        default: return;
      }
      const featureLabel = AI_FEATURES.find(f => f.key === featureKey)?.label || featureKey;
      setResult({ text: res.data.result, feature: featureLabel, timestamp: new Date().toISOString() });
      setHistory(h => [{ feature: featureLabel, timestamp: new Date().toISOString(), preview: res.data.result?.substring(0, 100) }, ...h].slice(0, 20));
    } catch (err) {
      toast.error(err.response?.data?.error || 'AI request failed. Check your Gemini API key.');
    } finally {
      setLoading(false);
    }
  };

  const handleAsk = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setActiveFeature('ask');
    setResult(null);
    try {
      const res = await aiAPI.ask({ query, project_id: selectedProject || undefined });
      setResult({ text: res.data.result, feature: 'AI Answer', timestamp: new Date().toISOString(), query });
      setHistory(h => [{ feature: 'Question', timestamp: new Date().toISOString(), preview: query.substring(0, 100) }, ...h].slice(0, 20));
      setQuery('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'AI request failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleTaskDesc = async () => {
    if (!query.trim()) { toast.error('Enter a task title to generate a description.'); return; }
    setLoading(true);
    setActiveFeature('taskdesc');
    setResult(null);
    try {
      const res = await aiAPI.generateTaskDescription({ title: query, project_id: selectedProject || undefined });
      setResult({ text: res.data.result, feature: 'Task Description', timestamp: new Date().toISOString() });
      setQuery('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to generate description.');
    } finally {
      setLoading(false);
    }
  };

  const copyResult = () => {
    if (result?.text) {
      navigator.clipboard.writeText(result.text);
      toast.success('Copied to clipboard');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Top Header Card */}
      <div className="rfp-detail-top-card">
        <div className="rfp-detail-header-main">
          <div>
            <div className="rfp-detail-title-wrap">
              <div
                style={{
                  width: 36,
                  height: 36,
                  background: 'linear-gradient(135deg, #0038A7 0%, #00AFC0 100%)',
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ffffff',
                  boxShadow: '0 2px 6px rgba(0, 56, 167, 0.25)'
                }}
              >
                <Brain size={20} />
              </div>
              <h1 className="rfp-detail-title">AI Assistant & Intelligence Engine</h1>
              <span
                className="badge"
                style={{
                  background: 'linear-gradient(135deg, rgba(0, 56, 167, 0.08), rgba(0, 175, 192, 0.12))',
                  color: '#0038A7',
                  border: '1px solid rgba(0, 56, 167, 0.2)',
                  fontWeight: 650,
                  fontSize: '0.74rem'
                }}
              >
                <Sparkles size={12} style={{ display: 'inline', marginRight: 4, verticalAlign: -1 }} />
                PROPEL AI
              </span>
            </div>
            <div className="rfp-detail-submeta">
              <span className="rfp-detail-submeta-item">
                <Brain size={14} style={{ color: '#0038A7' }} />
                Instant generative insights, cross-project risk audits, and predictive forecasting
              </span>
              <span className="rfp-detail-submeta-item">
                <Target size={14} style={{ color: '#64748b' }} />
                {projects.length} Active Projects In Scope
              </span>
            </div>
          </div>

          {!aiAvailable && (
            <div
              style={{
                padding: '8px 14px',
                background: '#fffbeb',
                border: '1px solid #fde68a',
                borderRadius: 8,
                fontSize: '0.82rem',
                color: '#92400e',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              <AlertTriangle size={15} color="#d97706" />
              <span>Gemini API key not configured in server .env</span>
            </div>
          )}
        </div>
      </div>

      {/* Scope & Ask Input Card */}
      <div
        className="card"
        style={{
          padding: '18px 22px',
          background: '#ffffff',
          borderRadius: 12,
          border: '1px solid var(--px-border)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 650, color: 'var(--px-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Project Context:
          </span>
          <div className="form-select-wrapper" style={{ margin: 0, minWidth: 280, maxWidth: 420 }}>
            <select
              className="form-select"
              value={selectedProject}
              onChange={e => setSelectedProject(e.target.value)}
              style={{ height: 38, fontSize: '0.85rem', fontWeight: 550 }}
            >
              <option value="">All Projects (Org-wide Analytics)</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.project_code})
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="form-select-chevron" />
          </div>
        </div>

        {/* Input Bar */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 260, position: 'relative' }}>
            <Sparkles size={16} style={{ position: 'absolute', left: 14, top: 12, color: '#0038A7' }} />
            <input
              className="form-input"
              style={{ paddingLeft: 40, height: 42, fontSize: '0.9rem', borderRadius: 8 }}
              placeholder={selectedProject ? 'Ask Propel AI anything about this project...' : 'Ask about all projects, resource bottlenecks, tender deliverables...'}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleAsk()}
              disabled={loading}
            />
          </div>
          <button
            className="btn btn-primary"
            onClick={handleAsk}
            disabled={loading || !query.trim()}
            style={{ minWidth: 110, height: 42, display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Send size={14} /> Ask AI
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleTaskDesc}
            disabled={loading || !query.trim()}
            title="Generate task description from title"
            style={{ height: 42, display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <FileText size={14} /> Draft Task
          </button>
        </div>

        {/* Prompt Suggestions */}
        <div style={{ fontSize: '0.76rem', color: 'var(--px-text-muted)', marginTop: 12, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, color: 'var(--px-text-secondary)' }}>Suggested Queries:</span>
          <span
            style={{ cursor: 'pointer', color: '#0038A7' }}
            onClick={() => setQuery("How many project tasks are currently overdue?")}
          >
            "How many project tasks are overdue?"
          </span>
          &bull;
          <span
            style={{ cursor: 'pointer', color: '#0038A7' }}
            onClick={() => setQuery("What are the top 3 high-risk project deliverables?")}
          >
            "What are the top 3 high-risk deliverables?"
          </span>
          &bull;
          <span
            style={{ cursor: 'pointer', color: '#0038A7' }}
            onClick={() => setQuery("Who has the highest task workload this month?")}
          >
            "Who is overloaded across active projects?"
          </span>
        </div>
      </div>

      {/* Feature Actions Grid */}
      <div>
        <div style={{ fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: 'var(--px-text-secondary)', marginBottom: 12 }}>
          One-Click AI Syntheses & Reports
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
          {AI_FEATURES.map(f => {
            const isActive = activeFeature === f.key;
            return (
              <div
                key={f.key}
                className="card"
                onClick={() => handleFeature(f.key)}
                style={{
                  cursor: loading ? 'wait' : 'pointer',
                  padding: '16px 18px',
                  borderRadius: 10,
                  transition: 'all 0.18s ease',
                  borderColor: isActive ? '#0038A7' : 'var(--px-border)',
                  borderWidth: isActive ? '2px' : '1px',
                  boxShadow: isActive ? '0 4px 12px rgba(0, 56, 167, 0.1)' : undefined,
                  background: isActive ? '#eff6ff' : '#ffffff',
                  opacity: loading && !isActive ? 0.6 : 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
                onMouseEnter={e => {
                  if (!isActive) {
                    e.currentTarget.style.borderColor = '#cbd5e1';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={e => {
                  if (!isActive) {
                    e.currentTarget.style.borderColor = 'var(--px-border)';
                    e.currentTarget.style.transform = 'none';
                  }
                }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `${f.color}15`,
                        color: f.color,
                        flexShrink: 0
                      }}
                    >
                      <f.icon size={18} />
                    </div>
                    <span style={{ fontWeight: 650, fontSize: '0.88rem', color: '#0b1220' }}>{f.label}</span>
                  </div>
                  <p style={{ fontSize: '0.78rem', color: 'var(--px-text-muted)', lineHeight: 1.45, margin: 0 }}>
                    {f.desc}
                  </p>
                </div>
                {f.requiresProject && !selectedProject && (
                  <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#d97706', marginTop: 8, display: 'block' }}>
                    &bull; Requires selecting a project scope
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Loading State Card */}
      {loading && (
        <div
          className="card"
          style={{
            textAlign: 'center',
            padding: '44px 20px',
            background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)',
            border: '1px solid #bfdbfe'
          }}
        >
          <div className="spinner" style={{ margin: '0 auto 16px', borderTopColor: '#0038A7' }} />
          <h3 style={{ fontWeight: 700, fontSize: '1.05rem', color: '#0b1220', marginBottom: 6 }}>
            Propel AI is analyzing project telemetry...
          </h3>
          <p style={{ fontSize: '0.84rem', color: 'var(--px-text-secondary)', maxWidth: 500, margin: '0 auto' }}>
            Synthesizing milestones, tasks, and commercial data using Gemini AI models.
          </p>
        </div>
      )}

      {/* Result Display Card */}
      {result && !loading && (
        <div
          ref={resultRef}
          className="card"
          style={{
            padding: 0,
            overflow: 'hidden',
            borderRadius: 12,
            border: '1px solid var(--px-border)',
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.04)'
          }}
        >
          <div
            style={{
              padding: '14px 22px',
              background: '#f8fafc',
              borderBottom: '1px solid var(--px-border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 10
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  background: 'rgba(0, 56, 167, 0.08)',
                  color: '#0038A7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Brain size={16} />
              </div>
              <div>
                <span style={{ fontWeight: 700, fontSize: '0.92rem', color: '#0b1220' }}>
                  {result.feature}
                </span>
                <span style={{ fontSize: '0.74rem', color: 'var(--px-text-muted)', marginLeft: 8 }}>
                  Generated at {new Date(result.timestamp).toLocaleTimeString()}
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={copyResult} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <Copy size={13} /> Copy Result
              </button>
              {activeFeature && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleFeature(activeFeature)}
                  disabled={loading}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                >
                  <RefreshCw size={13} /> Regenerate
                </button>
              )}
            </div>
          </div>

          {result.query && (
            <div style={{ padding: '12px 22px', background: '#eff6ff', borderBottom: '1px solid #bfdbfe', fontSize: '0.88rem', color: '#1e3a8a' }}>
              <strong>Prompt:</strong> {result.query}
            </div>
          )}

          <div
            style={{
              padding: '24px 28px',
              fontSize: '0.88rem',
              lineHeight: 1.75,
              color: '#1e293b',
              background: '#ffffff'
            }}
            dangerouslySetInnerHTML={{ __html: renderMarkdown(result.text) }}
          />
        </div>
      )}

      {/* History Panel */}
      {history.length > 0 && !loading && !result && (
        <div className="card" style={{ padding: '20px 22px' }}>
          <div className="card-header" style={{ marginBottom: 12, borderBottom: '1px solid var(--px-border)', paddingBottom: 10 }}>
            <div>
              <div className="card-title" style={{ fontSize: '0.95rem', fontWeight: 700 }}>Recent AI Syntheses</div>
              <div className="card-subtitle">Session history of previous analytical queries</div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {history.map((h, i) => (
              <div
                key={i}
                style={{
                  padding: '10px 12px',
                  borderRadius: 6,
                  borderBottom: '1px solid var(--px-border)',
                  display: 'flex',
                  gap: 12,
                  alignItems: 'center'
                }}
              >
                <Sparkles size={14} style={{ color: '#0038A7', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#0b1220' }}>{h.feature}</span>
                  <span style={{ fontSize: '0.78rem', color: 'var(--px-text-muted)', marginLeft: 8 }}>{h.preview}...</span>
                </div>
                <span style={{ fontSize: '0.72rem', color: 'var(--px-text-muted)', whiteSpace: 'nowrap' }}>
                  {new Date(h.timestamp).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAssistantPage;
